import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { FormikErrors, FormikHelpers } from 'formik';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';

import type { ApiError } from '@monetr/interface/api/client';
import Flex from '@monetr/interface/components/Flex';
import FormButton from '@monetr/interface/components/FormButton';
import FormTextField from '@monetr/interface/components/FormTextField';
import { layoutVariants } from '@monetr/interface/components/Layout';
import MCaptcha from '@monetr/interface/components/MCaptcha';
import MForm from '@monetr/interface/components/MForm';
import MLogo from '@monetr/interface/components/MLogo';
import BetaCodeInput from '@monetr/interface/components/register/BetaCodeInput';
import TextLink from '@monetr/interface/components/TextLink';
import Typography from '@monetr/interface/components/Typography';
import { useAppConfiguration } from '@monetr/interface/hooks/useAppConfiguration';
import useSignUp, { type SignUpResponse } from '@monetr/interface/hooks/useSignUp';
import { getLocale, getTimezone } from '@monetr/interface/util/locale';
import type { APIError } from '@monetr/interface/util/request';
import verifyEmailAddress from '@monetr/interface/util/verifyEmailAddress';

import styles from './register.module.scss';

interface RegisterValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
  captcha?: string;
  betaCode?: string;
}

const initialValues: RegisterValues = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  confirmPassword: '',
};

function validator(values: RegisterValues): FormikErrors<RegisterValues> {
  const errors: FormikErrors<RegisterValues> = {};

  if (values?.firstName.length < 2) {
    errors.firstName = 'First name must have at least 2 characters.';
  }

  if (values?.lastName.length < 2) {
    errors.lastName = 'Last name must have at least 2 characters.';
  }

  if (values?.email.length === 0) {
    errors.email = 'Email must be provided.';
  }

  if (values?.email && !verifyEmailAddress(values?.email)) {
    errors.email = 'Email must be valid.';
  }

  if (values?.password.length < 8) {
    errors.password = 'Password must be at least 8 characters long.';
  }

  if (values?.confirmPassword !== values?.password) {
    errors.confirmPassword = 'Password confirmation must match.';
  }

  if (values?.password.length > 71) {
    errors.password = 'Password is too long, must be less than 72 characters.';
  }

  return errors;
}

export function RegisterSuccessful(): JSX.Element {
  return (
    <div className={styles.registerPageRoot}>
      <MLogo className={layoutVariants({ size: 'logo' })} />
      <Typography align='center' className='max-w-md' size='xl' weight='medium'>
        A verification message has been sent to your email address, please verify your email.
      </Typography>
      <div className='mt-1 flex justify-center gap-1 flex-col md:flex-row items-center'>
        <Typography color='subtle' size='sm'>
          Return to
        </Typography>
        <TextLink size='sm' to='/login'>
          Sign in
        </TextLink>
      </div>
    </div>
  );
}

export default function Register(): JSX.Element {
  const { enqueueSnackbar } = useSnackbar();
  const { data: config } = useAppConfiguration();
  const signUp = useSignUp();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [successful, setSuccessful] = useState(false);

  async function submit(values: RegisterValues, helpers: FormikHelpers<RegisterValues>): Promise<void> {
    helpers.setSubmitting(true);

    return await signUp({
      betaCode: values.betaCode,
      captcha: values.captcha,
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      password: values.password,
      timezone: getTimezone(),
      locale: getLocale(),
    })
      .then((result: SignUpResponse) => {
        // After sending the sign up request, if the user needs to verify their email then the requires verification
        // field will be true. We can stop here and just show the user a successful screen.
        if (result.requireVerification) {
          return setSuccessful(true);
        }

        return queryClient.invalidateQueries({ queryKey: ['/api/users/me'] }).then(() => {
          // If the register endpoint has told us to navigate to a specific url afterwards, then do that now.
          if (result.nextUrl) {
            return navigate(result.nextUrl);
          }

          // Otherwise just go to the index-ish route for the authenticated app.
          return navigate('/');
        });
      })
      .catch((error: ApiError<APIError>) => {
        const message = error.response.data.error || 'Failed to sign up.';
        enqueueSnackbar(message, {
          variant: 'error',
          disableWindowBlurListener: true,
        });
      })
      .finally(() => helpers.setSubmitting(false));
  }

  if (successful) {
    return <RegisterSuccessful />;
  }

  return (
    <div className={styles.registerPageRoot}>
      <MForm
        className='flex flex-col md:w-1/2 lg:w-1/3 xl:w-1/4 items-center gap-1'
        initialValues={initialValues}
        onSubmit={submit}
        validate={validator}
      >
        <div className='max-w-[96px] w-full'>
          <MLogo />
        </div>
        <Flex align='center' orientation='column'>
          <Typography align='center' size='5xl'>
            Get Started
          </Typography>
          <Typography align='center' color='subtle' size='lg'>
            Create your monetr account now
          </Typography>
        </Flex>
        <Flex gap='sm' orientation='stackSmall'>
          <FormTextField
            autoFocus
            className='w-full'
            data-testid='register-first-name'
            label='First Name'
            name='firstName'
            required
            type='text'
          />
          <FormTextField
            className='w-full'
            data-testid='register-last-name'
            label='Last Name'
            name='lastName'
            required
            type='text'
          />
        </Flex>
        <FormTextField
          autoComplete='email'
          className='w-full'
          data-testid='register-email'
          label='Email Address'
          name='email'
          required
          type='email'
        />
        <FormTextField
          autoComplete='new-password'
          className='w-full'
          data-testid='register-password'
          label='Password'
          name='password'
          required
          type='password'
        />
        <FormTextField
          autoComplete='new-password'
          className='w-full'
          data-testid='register-confirm-password'
          label='Confirm Password'
          name='confirmPassword'
          required
          type='password'
        />
        <BetaCodeInput />
        <MCaptcha className='mb-1' name='captcha' show={Boolean(config?.verifyRegister)} />
        <FormButton className='w-full mt-1' data-testid='register-submit' role='form' type='submit' variant='primary'>
          Sign Up
        </FormButton>
        <div className='mt-1 flex justify-center gap-1 flex-col md:flex-row items-center'>
          <Typography className='gap-1 inline-block text-center' color='subtle' component='p' size='sm'>
            By signing up you agree to monetr's&nbsp;
            <a
              className='text-dark-monetr-blue hover:underline focus:ring-2 focus:ring-dark-monetr-blue focus:underline'
              href='https://monetr.app/policy/terms'
              rel='noopener noreferrer'
              target='_blank'
            >
              Terms & Conditions
            </a>{' '}
            and&nbsp;
            <a
              className='text-dark-monetr-blue hover:underline focus:ring-2 focus:ring-dark-monetr-blue focus:underline'
              href='https://monetr.app/policy/privacy'
              rel='noopener noreferrer'
              target='_blank'
            >
              Privacy Policy
            </a>
          </Typography>
        </div>
        <div className='mt-1 flex justify-center gap-1 flex-col md:flex-row items-center'>
          <Typography color='subtle' size='sm'>
            Already have an account?
          </Typography>
          <TextLink size='sm' to='/login'>
            Sign in instead
          </TextLink>
        </div>
      </MForm>
    </div>
  );
}
